// eslint-disable-next-line
import { Dictionary } from 'vue-router/types/router';
import { EstimateShipAddressType } from '@/features/estimate/types';
import {
  PackageItem,
  PackageType, ShipFrom, ShipmentExtras, ShipTo, SignatureType, AppointmentType,
} from '@/features/shipment/types';
import {
  getAppointmentDefaultDate, getAppointmentTime, isValidDateString, isValidTimeString,
} from '@/features/shipment/helpers/createShipment';

export type query = Dictionary<string | (string | null)[]>

export type fieldValue = string | boolean | number;

export interface ParsedQueryData {
  fromAddress: ShipFrom | EstimateShipAddressType | null,
  toAddress: ShipTo | EstimateShipAddressType | null,
  packages: PackageItem[],
  extras: {
    comments: string,
    signature: string,
    reference: string,
  },
  isDocumentsOnly: boolean,
  emailNotifications: string,
  isDebug: boolean,
}

export const parseValueFromString = (str: string, isBoolean = false): fieldValue => {
  if (!str) {
    return '';
  }

  if (isBoolean) {
    const lowerStr = str.toString().toLowerCase();

    if (lowerStr === 'false') {
      return false;
    }

    if (lowerStr === 'true') {
      return true;
    }

    // handle 1 or 0 value
    if (!Number.isNaN(+str)) {
      return !!+str;
    }

    return false;
  }

  if (!Number.isNaN(+str)) {
    return +str;
  }

  return str;
};

export const parseQueryField = (
  query: query,
  fieldName: string,
  isBoolean = false,
): fieldValue => {
  if (!fieldName || !query) {
    return '';
  }

  const value = query[Object.keys(query)
    .find((key) => {
      return key.toLowerCase() === fieldName.toLowerCase();
    }) || ''];

  if (!value) {
    return '';
  }

  let finalField;

  // handle several similar queries
  if (Array.isArray(value)) {
    finalField = value[value.length - 1];
  } else {
    finalField = value;
  }

  return parseValueFromString(finalField, isBoolean);
};

export const parseAddress = (
  query: query,
  destination: 'From' | 'To' = 'From',
  isEstimate = false,
): ShipTo | EstimateShipAddressType | null => {
  const getFieldName = (name) => `${destination}${name}`;

  const company = parseQueryField(query, getFieldName('Company'))?.toString() as string;
  const phone = parseQueryField(query, getFieldName('Tel'))?.toString();
  const email = parseQueryField(query, getFieldName('Email'))?.toString() as string;
  const addr1 = parseQueryField(query, getFieldName('Addr1'))?.toString() as string;
  const countryCode = (parseQueryField(query, getFieldName('CC'))?.toString() as string).toUpperCase();
  const postalCode = parseQueryField(query, getFieldName('PC'))?.toString();
  const city = parseQueryField(query, getFieldName('City'))?.toString() as string;
  const contact = parseQueryField(query, getFieldName('Contact'))?.toString() as string;
  const residential = parseQueryField(query, getFieldName('Residential'), true) as boolean || false;
  const isInside = parseQueryField(query, getFieldName('IsInside'), true) as boolean || false;
  const isLimitedAccess = parseQueryField(query, getFieldName('IsLimitedAccess'), true) as boolean || false;
  const isTailGate = parseQueryField(query, getFieldName('IsTailGate'), true) as boolean || false;
  const isTradeShow = parseQueryField(query, getFieldName('IsTradeShow'), true) as boolean || false;
  const appointment = parseQueryField(query, getFieldName('Appointment'), true) as boolean || false;
  const appointmentDate = parseQueryField(query, getFieldName('AppointmentDate'))?.toString() as string;
  const appointmentTime = parseQueryField(query, getFieldName('AppointmentTime'))?.toString() as string;

  function parseAppointment() {
    const queryAppointmentDate = isValidDateString(appointmentDate) ? appointmentDate : undefined;
    const queryAppointmentTime = isValidTimeString(appointmentTime) ? appointmentTime : undefined;

    const date = getAppointmentDefaultDate(queryAppointmentDate);
    const time = getAppointmentTime(queryAppointmentTime);

    const appointmentType: keyof typeof AppointmentType = date ? 'Scheduled' : 'Required';

    if (appointment) {
      return { appointment: { appointmentType, date, time } };
    }
    return undefined;
  }

  const estimateData = {
    countryCode,
    postalCode,
    city,
    isInside,
    isLimitedAccess,
    isTailGate,
    isTradeShow,
    ...(destination === 'To' && parseAppointment()),
  };

  if (isEstimate) {
    return { ...estimateData, ...(destination === 'To' && { residential }) };
  }

  return {
    company,
    contact,
    phone,
    emails: [email],
    addr1,
    addr2: parseQueryField(query, getFieldName('Addr2'))?.toString() as string,
    addr3: parseQueryField(query, getFieldName('Addr3'))?.toString() as string,
    province: parseQueryField(query, getFieldName('Prov'))?.toString() as string,
    residential,
    ...estimateData,
  };
};

export const parsePackages = (query: query): PackageItem[] => {
  const packageCount = Object.keys(query)
    .filter((key) => key.slice(0, 2).toLowerCase() === 'pt')
    .length;
  const packages: PackageItem[] = [];

  const parsePackageType = (value: string): keyof typeof PackageType | null => {
    if (!value || typeof value !== 'string') {
      return null;
    }

    let finalValue: keyof typeof PackageType | null | string = value;

    if (value.toLowerCase() === 'pak') {
      finalValue = PackageType.Pak;
    } else if (value.toLowerCase() === 'mypackage') {
      finalValue = PackageType.MyPackage;
    } else if (value.toLowerCase() === 'env') {
      finalValue = PackageType.Envelope;
    } else {
      finalValue = null;
    }

    return finalValue as keyof typeof PackageType;
  };

  for (let i = 1; i <= packageCount; i += 1) {
    const packageType = parsePackageType(parseQueryField(query, `PT${i}`) as string);
    const weight = packageType === PackageType.Envelope
      ? 1
      : parseQueryField(query, `Weight${i}`) as number;

    if (!Number.isNaN(+weight) && weight && packageType) {
      const length = packageType === PackageType.MyPackage ? parseQueryField(query, `L${i}`) as number || 0 : 0;
      const width = packageType === PackageType.MyPackage ? parseQueryField(query, `W${i}`) as number || 0 : 0;
      const height = packageType === PackageType.MyPackage ? parseQueryField(query, `H${i}`) as number || 0 : 0;

      let isAdditionalHandling = false;
      if (packageType === PackageType.MyPackage) {
        isAdditionalHandling = parseQueryField(query, `AH${i}`, true) as boolean || false;
      }

      packages.push({
        packageType,
        weight,
        length,
        width,
        height,
        insurance: parseQueryField(query, `DV${i}`) as number || 0,
        isAdditionalHandling,
        dimUnits: 'Inches',
        weightUnits: 'Lbs',
      });
    }
  }

  return packages;
};

export const parseExtras = (query: query): ShipmentExtras => {
  const comments = parseQueryField(query, 'Comments') as string;
  const finalComments = comments.length <= 50 ? comments : '';
  const reference = parseQueryField(query, 'Ref') as string;
  const finalReference = reference.length <= 20 ? reference : '';
  let signature = parseQueryField(query, 'Signature') as string;

  if (signature.toLowerCase() === 'signature') {
    signature = SignatureType.SignatureRequired;
  } else if (signature.toLowerCase() === 'adult') {
    signature = SignatureType.AdultSignatureRequired;
  } else {
    signature = SignatureType.None;
  }

  return {
    comments: finalComments,
    reference: finalReference,
    signature: signature as (keyof typeof SignatureType),
  };
};

export const parseQueryString = (query: query, isEstimate = false): ParsedQueryData => {
  return {
    fromAddress: parseAddress(query, 'From', isEstimate),
    toAddress: parseAddress(query, 'To', isEstimate),
    extras: parseExtras(query),
    packages: parsePackages(query),
    isDebug: parseQueryField(query, 'Debug', true) as boolean,
    isDocumentsOnly: parseQueryField(query, 'isDocumentsOnly', true) as boolean || false,
    emailNotifications: parseQueryField(query, 'emailNotifications') as string,
  };
};
